import { MenuStructure, RouteConfig } from './route-config';

/**
 * Two ways a vertical extends the shared sidebar.
 *
 * A family with children every unit type uses — Master lists a vertical's
 * masters next to customers — takes a `MenuContribution`: no top-level group
 * names the vertical, only a named sub-family inside the shared one once it
 * has enough items to read as a set (`withMenuContributions`). A family that
 * is one vertical's own — its Sales, its Production — shares nothing with
 * another vertical's family of the same name, so it is declared as a full
 * `MenuStructure` carrying its own `unitType` and spliced in with
 * `withMenuGroupsAfter`; the shell drops it for every other tenant
 * (`groupsForUnitType`, ADR-P-002 v2.0, amending D6). Which items a tenant
 * actually sees inside either is still decided by the operations its
 * database holds (seeded by unit-type scope from the admin catalogue) and
 * the user's roles, exactly as for every other item.
 */
export interface MenuContribution {
  /** A `MenuStructure.id` such as 'master'. */
  groupId: string;
  children: RouteConfig[];
}

/**
 * Returns the groups with each contribution appended to its family. Pure: the
 * inputs are not mutated. A contribution naming a group that does not exist
 * becomes a group of its own, labelled by its id, so a typo shows up on screen
 * instead of silently dropping a vertical's entries.
 */
export function withMenuContributions(
  groups: readonly MenuStructure[],
  contributions: readonly MenuContribution[]
): MenuStructure[] {
  const byGroup = new Map<string, RouteConfig[]>();
  for (const c of contributions) {
    byGroup.set(c.groupId, [...(byGroup.get(c.groupId) ?? []), ...c.children]);
  }

  const out: MenuStructure[] = groups.map((g) => {
    const extra = byGroup.get(g.id);
    if (!extra) return g;
    byGroup.delete(g.id);
    return { ...g, children: [...(g.children ?? []), ...extra] };
  });

  // A group the configuration does not describe still has to render, so the
  // fields `MenuStructure` requires are filled from the id: the entries show
  // up under a heading nobody meant to create, which is the point.
  for (const [id, children] of byGroup) {
    out.push({ id, label: id, icon: 'appstore', openMapKey: id, dataLabel: id, children });
  }

  return out;
}

/**
 * Returns `groups` with `extra` spliced in immediately after the group named
 * `anchorId` (appended at the end if no group has that id, so a renamed or
 * removed anchor loses nothing, only its position). Pure: `groups` is not
 * mutated. This is how a vertical's own top-level families join the sidebar —
 * see the `MenuContribution` doc above for when a family belongs here instead
 * of behind `withMenuContributions`.
 */
export function withMenuGroupsAfter(
  groups: readonly MenuStructure[],
  anchorId: string,
  extra: readonly MenuStructure[]
): MenuStructure[] {
  const at = groups.findIndex((g) => g.id === anchorId);
  const index = at === -1 ? groups.length : at + 1;
  return [...groups.slice(0, index), ...extra, ...groups.slice(index)];
}
